"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import { ROOMS, getMatchingProblems, type Room, type Symptom, type DiagnoseResult } from "@/lib/diagnose-data";

type Severity = "just-noticed" | "getting-worse" | "urgent";
type Step = "room" | "symptom" | "severity" | "results";

const SEVERITIES: { id: Severity; label: string; description: string }[] = [
  { id: "just-noticed", label: "Just noticed it", description: "It appeared recently and is not causing damage yet" },
  { id: "getting-worse", label: "Getting worse", description: "It has been around for a while and seems to be worsening" },
  { id: "urgent", label: "Urgent or causing damage", description: "It needs attention now, there may be a safety risk or active damage" },
];

const urgencyConfig = {
  cosmetic: { label: "Cosmetic", bg: "bg-green-100 text-green-800", dot: "bg-green-500" },
  fix_soon: { label: "Fix Soon", bg: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  urgent: { label: "Urgent", bg: "bg-red-100 text-red-800", dot: "bg-red-500" },
};

export function DiagnoseClient() {
  const [step, setStep] = useState<Step>("room");
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [selectedSymptom, setSelectedSymptom] = useState<Symptom | null>(null);
  const [selectedSeverity, setSelectedSeverity] = useState<Severity | null>(null);
  const [results, setResults] = useState<DiagnoseResult[]>([]);

  const goBack = useCallback(() => {
    if (step === "symptom") {
      setStep("room");
      setSelectedRoom(null);
    } else if (step === "severity") {
      setStep("symptom");
      setSelectedSymptom(null);
    } else if (step === "results") {
      setStep("severity");
      setSelectedSeverity(null);
      setResults([]);
    }
  }, [step]);

  const selectRoom = useCallback((room: Room) => {
    setSelectedRoom(room);
    setStep("symptom");
  }, []);

  const selectSymptom = useCallback((symptom: Symptom) => {
    setSelectedSymptom(symptom);
    setStep("severity");
  }, []);

  const selectSeverity = useCallback(
    (severity: Severity) => {
      setSelectedSeverity(severity);
      if (selectedSymptom) {
        const matches = getMatchingProblems(selectedSymptom.problemSlugs, severity);
        setResults(matches);
      }
      setStep("results");
    },
    [selectedSymptom]
  );

  const startOver = useCallback(() => {
    setStep("room");
    setSelectedRoom(null);
    setSelectedSymptom(null);
    setSelectedSeverity(null);
    setResults([]);
  }, []);

  const stepNumber = step === "room" ? 1 : step === "symptom" ? 2 : step === "severity" ? 3 : 4;

  return (
    <div className="max-w-2xl mx-auto px-5 md:px-8 pt-28 pb-16">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-serif text-[28px] md:text-[40px] font-bold text-charcoal leading-tight mb-3">
          What&apos;s Wrong With My House?
        </h1>
        <p className="text-text-body text-[15px] md:text-[17px] max-w-lg mx-auto leading-relaxed">
          Answer three quick questions and we&apos;ll identify the most likely problems, how urgent they are, and what to do next.
        </p>
      </div>

      {/* Progress */}
      {step !== "results" && (
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  n < stepNumber
                    ? "bg-forest text-white"
                    : n === stepNumber
                      ? "bg-forest text-white"
                      : "bg-stone text-text-muted"
                }`}
              >
                {n < stepNumber ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  n
                )}
              </div>
              {n < 3 && (
                <div className={`w-12 h-0.5 ${n < stepNumber ? "bg-forest" : "bg-stone"}`} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Back button */}
      {step !== "room" && (
        <button
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-forest mb-5 bg-transparent border-none cursor-pointer font-sans transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
      )}

      {/* Step 1: Room */}
      {step === "room" && (
        <div>
          <h2 className="font-serif text-[20px] md:text-[24px] font-semibold text-charcoal mb-1.5">
            Where is the problem?
          </h2>
          <p className="text-sm text-text-muted mb-5">Select the room or area of your home.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {ROOMS.map((room) => (
              <button
                key={room.id}
                onClick={() => selectRoom(room)}
                className="flex flex-col items-center gap-2 p-5 bg-white rounded-md border border-stone text-center cursor-pointer transition-all hover:border-sage hover:shadow-[0_2px_12px_rgba(0,0,0,0.06)] active:scale-[0.98] font-sans group"
              >
                <span className="text-2xl">{room.icon}</span>
                <span className="text-sm font-semibold text-charcoal group-hover:text-forest transition-colors">
                  {room.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Symptom */}
      {step === "symptom" && selectedRoom && (
        <div>
          <h2 className="font-serif text-[20px] md:text-[24px] font-semibold text-charcoal mb-1.5">
            What kind of issue?
          </h2>
          <p className="text-sm text-text-muted mb-5">
            You selected <span className="font-medium text-charcoal">{selectedRoom.icon} {selectedRoom.label}</span>. What best describes the problem?
          </p>
          <div className="flex flex-col gap-2.5">
            {selectedRoom.symptoms.map((symptom) => (
              <button
                key={symptom.id}
                onClick={() => selectSymptom(symptom)}
                className="flex items-center justify-between w-full p-4 bg-white rounded-md border border-stone text-left cursor-pointer transition-all hover:border-sage hover:shadow-[0_2px_12px_rgba(0,0,0,0.06)] active:scale-[0.99] font-sans group"
              >
                <span className="text-[15px] font-medium text-charcoal group-hover:text-forest transition-colors">
                  {symptom.label}
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted shrink-0 ml-3">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Severity */}
      {step === "severity" && (
        <div>
          <h2 className="font-serif text-[20px] md:text-[24px] font-semibold text-charcoal mb-1.5">
            How bad is it?
          </h2>
          <p className="text-sm text-text-muted mb-5">This helps us prioritise the most relevant problems.</p>
          <div className="flex flex-col gap-3">
            {SEVERITIES.map((s) => (
              <button
                key={s.id}
                onClick={() => selectSeverity(s.id)}
                className="flex flex-col w-full p-4 bg-white rounded-md border border-stone text-left cursor-pointer transition-all hover:border-sage hover:shadow-[0_2px_12px_rgba(0,0,0,0.06)] active:scale-[0.99] font-sans group"
              >
                <span className="text-[15px] font-semibold text-charcoal group-hover:text-forest transition-colors">
                  {s.label}
                </span>
                <span className="text-sm text-text-muted mt-0.5">{s.description}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {step === "results" && (
        <div>
          <div className="mb-6">
            <h2 className="font-serif text-[20px] md:text-[24px] font-semibold text-charcoal mb-1.5">
              Your likely problems
            </h2>
            <p className="text-sm text-text-muted">
              Based on{" "}
              <span className="font-medium text-charcoal">{selectedRoom?.icon} {selectedRoom?.label}</span>
              {" / "}
              <span className="font-medium text-charcoal">{selectedSymptom?.label}</span>
              {" / "}
              <span className="font-medium text-charcoal">
                {SEVERITIES.find((s) => s.id === selectedSeverity)?.label}
              </span>
            </p>
          </div>

          {results.length > 0 ? (
            <div className="flex flex-col gap-4 mb-8">
              {results.map((r, i) => {
                const uc = urgencyConfig[r.problem.urgency_level];
                return (
                  <Link
                    key={r.problem.slug}
                    href={`/problems/${r.problem.slug}`}
                    className="block bg-white rounded-md border border-stone p-5 no-underline transition-all hover:border-sage hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] group"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2.5">
                        <span className="text-xs font-bold text-text-muted bg-cream rounded-full w-6 h-6 flex items-center justify-center shrink-0">
                          {i + 1}
                        </span>
                        <h3 className="text-[16px] font-semibold text-charcoal group-hover:text-forest transition-colors">
                          {r.problem.title}
                        </h3>
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${uc.bg}`}>
                        {uc.label}
                      </span>
                    </div>

                    <p className="text-sm text-text-body leading-relaxed mb-3">
                      {r.problem.description}
                    </p>

                    <div className="text-xs text-text-muted mb-3">
                      <span className="font-medium text-charcoal">Likely causes: </span>
                      {r.problem.causes.slice(0, 2).join("; ")}
                      {r.problem.causes.length > 2 && "; and more"}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {r.costLow !== null && r.costHigh !== null && (
                          <span className="text-sm font-semibold text-forest">
                            Typical cost: £{r.costLow} &ndash; £{r.costHigh}
                          </span>
                        )}
                        <span className="text-xs text-text-muted">
                          {r.tradeName}
                        </span>
                      </div>
                      <span className="text-xs font-medium text-forest group-hover:underline">
                        Full details →
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="bg-cream rounded-md p-6 text-center mb-8">
              <p className="text-text-body text-sm">
                We couldn&apos;t find an exact match. Try a different symptom, or use Snap and Fix for an AI diagnosis.
              </p>
            </div>
          )}

          {/* Snap CTA */}
          <div className="bg-sage-pale rounded-md border border-sage-light/30 p-6 text-center mb-6">
            <p className="text-[15px] font-semibold text-charcoal mb-1.5">Not sure? Get an instant AI diagnosis</p>
            <p className="text-sm text-text-muted mb-4">
              Snap a photo of the problem and our AI will identify it, estimate the cost, and match you with local tradespeople.
            </p>
            <Link
              href="/snap"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-forest text-white text-sm font-semibold rounded-sm no-underline transition-colors hover:bg-forest-light"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              Snap and Fix
            </Link>
          </div>

          {/* Browse problems CTA */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <Link
              href="/problems"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-forest border border-forest rounded-sm no-underline hover:bg-forest hover:text-white transition-colors"
            >
              Browse All Problems
            </Link>
            <Link
              href="/cost-estimator"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-forest border border-forest rounded-sm no-underline hover:bg-forest hover:text-white transition-colors"
            >
              Cost Estimator
            </Link>
          </div>

          {/* Start over */}
          <div className="text-center">
            <button
              onClick={startOver}
              className="text-sm font-medium text-text-muted hover:text-forest bg-transparent border-none cursor-pointer font-sans transition-colors"
            >
              Start over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
